import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SITE } from "@/constants/site";
import { FadeUp, RevealLineInView } from "@/components/animations/Reveal";

export default function Contact({ onOpenMail }) {
    const { t } = useLanguage();

    return (
        <section id="contact" className="relative bg-cream py-16 md:py-36" data-testid="contact">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-4 mb-8">
                    <span className="chapter-num text-lg">— {t.contact.eyebrow}</span>
                    <span className="h-[1px] flex-1 bg-gold/40" />
                </div>

                <h2 className="font-display text-ink leading-[0.98] text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-16 max-w-3xl">
                    <RevealLineInView>{t.contact.title.split(" ").slice(0, 2).join(" ")}</RevealLineInView>{" "}
                    <span className="serif-italic text-gold">
                        <RevealLineInView delay={0.1}>{t.contact.title.split(" ").slice(2).join(" ")}</RevealLineInView>
                    </span>
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    {/* Info + Hours */}
                    <div className="lg:col-span-5 flex flex-col gap-6">
                        <FadeUp>
                            <div className="bg-white rounded-3xl p-8 border border-gold/25">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-11 h-11 rounded-full bg-cream flex items-center justify-center shrink-0">
                                        <MapPin className="w-5 h-5 text-gold-deep" />
                                    </div>
                                    <div>
                                        <div className="eyebrow mb-2">Adresse</div>
                                        <div className="text-ink font-sans">{SITE.address.street}</div>
                                        <div className="text-ink/70 font-sans">{SITE.address.city}</div>
                                        <div className="text-ink/70 font-sans">{SITE.address.country}</div>
                                    </div>
                                </div>

                                <div className="h-[1px] bg-gold/20 my-6" />

                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-11 h-11 rounded-full bg-cream flex items-center justify-center shrink-0">
                                        <Phone className="w-5 h-5 text-gold-deep" />
                                    </div>
                                    <div>
                                        <div className="eyebrow mb-2">Telefon</div>
                                        <a href={`tel:${SITE.phoneRaw}`} className="text-ink hover:text-gold transition-colors">
                                            {SITE.phone}
                                        </a>
                                    </div>
                                </div>

                                <div className="h-[1px] bg-gold/20 my-6" />

                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-11 h-11 rounded-full bg-cream flex items-center justify-center shrink-0">
                                        <MessageCircle className="w-5 h-5 text-gold-deep" />
                                    </div>
                                    <div>
                                        <div className="eyebrow mb-2">WhatsApp</div>
                                        <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-gold transition-colors" data-testid="contact-whatsapp">
                                            {SITE.phone}
                                        </a>
                                    </div>
                                </div>

                                <div className="h-[1px] bg-gold/20 my-6" />

                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-full bg-cream flex items-center justify-center shrink-0">
                                        <Mail className="w-5 h-5 text-gold-deep" />
                                    </div>
                                    <div>
                                        <div className="eyebrow mb-2">E-Mail</div>
                                        <button onClick={onOpenMail} className="text-ink hover:text-gold transition-colors break-all text-left" data-testid="contact-mail-open">
                                            {SITE.email}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </FadeUp>

                        <FadeUp delay={0.1}>
                            <div className="bg-white rounded-3xl p-8 border border-gold/25">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-11 h-11 rounded-full bg-cream flex items-center justify-center shrink-0">
                                        <Clock className="w-5 h-5 text-gold-deep" />
                                    </div>
                                    <div className="eyebrow">{t.contact.hours_title}</div>
                                </div>
                                <ul>
                                    {t.contact.days.map((d, i) => {
                                        const h = SITE.hours[i];
                                        const closed = !h.open;
                                        return (
                                            <li
                                                key={d}
                                                className="flex items-center justify-between py-3 border-b last:border-b-0 border-gold/15"
                                                data-testid={`hours-row-${i}`}
                                            >
                                                <span className="font-sans text-ink">{d}</span>
                                                <span className={`font-display italic ${closed ? "text-ink/40" : "text-gold"}`}>
                                                    {closed ? t.contact.closed : h.open}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Map */}
                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <div
                            className="relative w-full h-full min-h-[520px] rounded-3xl overflow-hidden border border-gold/25 bg-white"
                            data-testid="contact-map"
                        >
                            <iframe
                                title="Frizerka Standort"
                                src={SITE.mapEmbed}
                                width="100%"
                                height="100%"
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                                style={{ border: 0, position: "absolute", inset: 0, filter: "grayscale(0.25) contrast(1.05)" }}
                            />
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}
